// assets/uc-upsell-modal.js
(() => {
  // This file and the logo should both be uploaded to Shopify's theme assets.
  // A sibling URL avoids Liquid syntax inside a static .js asset.
  const upsellScriptUrl = document.currentScript?.src ||
    Array.from(document.scripts).find((script) => /(?:\/|^)uc-upsell-modal\.js(?:\?|$)/.test(script.src))?.src || '';
  const upsellLogoUrl = upsellScriptUrl
    ? new URL('uc-upsell-logo.png', upsellScriptUrl).href
    : '';

  const CONFIG = {
    enabled: true,
    showEveryTime: true,
    sessionStorageKey: 'uc_upsell_seen_session',

    brandName: 'UltraCore Systems',
    logoUrl: upsellLogoUrl,
    cardBg: '#111114',
    cardBg2: '#080809',
    textColor: '#F7F7F8',
    mutedTextColor: '#AFAFB4',
    accentColor: '#C8102E',
    accentColor2: '#E11E42',
    accentColor3: '#630916',
    highlightColor: '#FFFFFF',
    buttonTextColor: '#ffffff',
    badgeBg: '#1D1115',
    badgeBorder: '#73303B',
    badgeText: '#F7F7F8',

    defaultOffer: {
      upsellVariantId: 52619193745687,
      title: 'Boost Your Order',
      yesLabel: 'Yes, I want more entries!',
      noLabel: "No, I don't want more entries.",
      badgeText: 'LIMITED OFFER',
      noteText: 'UltraCore Systems special add-on'
    },

    upsellRules: [
      {
        minPriceCents: 0,
        maxPriceCents: 1999,
        offer: {
          upsellVariantId: 52688696639767,
          title: 'Add a Small Bonus',
          yesLabel: "Yes, I want more entries!",
          noLabel: "No, I don't want more entries.",
          badgeText: 'BONUS OFFER',
          noteText: 'Low-cost add-on'
        }
      },
      {
        minPriceCents: 2000,
        maxPriceCents: 4999,
        offer: {
          upsellVariantId: 52619193745687,
          title: 'Boost Your Entries',
          yesLabel: 'Yes, boost my order',
          noLabel: "No, I don't want to boost my order.",
          badgeText: 'POPULAR',
          noteText: 'Popular add-on'
        }
      },
      {
        minPriceCents: 5000,
        maxPriceCents: null,
        offer: {
          upsellVariantId: 52688694444311,
          title: 'Premium Boost',
          yesLabel: 'Yes, maximize my entries',
          noLabel: "No, I don't want to maximize my entries.",
          badgeText: 'BEST VALUE',
          noteText: 'Premium add-on'
        }
      }
    ]
  };

  let modalEl = null;
  let overlayEl = null;
  let messageEl = null;
  let yesBtn = null;
  let noBtn = null;
  let closeBtn = null;
  let priceNoteEl = null;
  let titleEl = null;
  let badgeEl = null;
  let entriesNumberEl = null;

  let currentAction = null;
  let isProcessing = false;
  let expressClickBound = false;

  const sourceVariantCache = new Map();
  const upsellVariantCache = new Map();

  function submitNativeProductForm(form) {
    if (!(form instanceof HTMLFormElement)) return;

    // Use a true native submit so we bypass this script's submit listener.
    HTMLFormElement.prototype.submit.call(form);
  }

  function isStandardProductFormAction(action) {
    return action && action.type === 'form' && action.form instanceof HTMLFormElement;
  }

  function shouldShowUpsellThisSession() {
    if (CONFIG.showEveryTime) return true;

    try {
      return sessionStorage.getItem(CONFIG.sessionStorageKey) !== '1';
    } catch {
      return true;
    }
  }

  function markUpsellSeenThisSession() {
    try {
      sessionStorage.setItem(CONFIG.sessionStorageKey, '1');
    } catch {}
  }

  function formatEntriesText(raw) {
    if (raw === null || raw === undefined || String(raw).trim() === '') {
      return 'extra';
    }

    const str = String(raw).trim();

    if (/^\d+(\.\d+)?$/.test(str)) {
      return new Intl.NumberFormat('en-US').format(Number(str));
    }

    return str;
  }

  function formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  function nextFrame() {
    return new Promise((resolve) => requestAnimationFrame(resolve));
  }

  function escapeHtml(str) {
    return String(str)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function forceUnlockPage() {
    document.documentElement.classList.remove('uc-upsell-lock');
    document.body.classList.remove('uc-upsell-lock');
  }

  function getUpsellEntriesFromMap(variantId) {
    const id = Number(variantId || 0);
    const raw = window.UC_UPSELL_META?.[id]?.entriesFixed;

    if (raw === null || raw === undefined || String(raw).trim() === '') {
      return null;
    }

    return raw;
  }

  async function fetchSourceVariantInfo(variantId) {
    const id = Number(variantId || 0);
    if (!id) throw new Error('Missing source variant id.');

    if (sourceVariantCache.has(id)) return sourceVariantCache.get(id);

    const res = await fetch(`/variants/${id}.js`, {
      credentials: 'same-origin'
    });

    if (!res.ok) {
      throw new Error('Failed to load source variant.');
    }

    const variant = await res.json();
    sourceVariantCache.set(id, variant);
    return variant;
  }

  async function fetchUpsellVariant(variantId) {
    const id = Number(variantId || 0);
    if (!id) {
      throw new Error('Missing upsell variant id.');
    }

    if (upsellVariantCache.has(id)) return upsellVariantCache.get(id);

    const res = await fetch(`/variants/${id}.js`, {
      credentials: 'same-origin'
    });

    if (!res.ok) {
      throw new Error('Failed to load upsell variant.');
    }

    const variant = await res.json();

    const mapped = {
      id: Number(variant.id),
      title: variant.name || variant.public_title || variant.title || 'Special offer',
      priceCents: Number(variant.price || 0),
      priceText: formatMoney(variant.price || 0),
      entriesFixed: getUpsellEntriesFromMap(id)
    };

    upsellVariantCache.set(id, mapped);
    return mapped;
  }

  function ruleMatches(rule, context) {
    const { variantId, priceCents } = context;

    const hasVariantRule = Array.isArray(rule.variantIds) && rule.variantIds.length > 0;
    const hasMinPrice = typeof rule.minPriceCents === 'number';
    const hasMaxPrice = typeof rule.maxPriceCents === 'number';

    const variantOk = !hasVariantRule || rule.variantIds.includes(variantId);
    const minOk = !hasMinPrice || priceCents >= rule.minPriceCents;
    const maxOk = !hasMaxPrice || priceCents <= rule.maxPriceCents;

    return variantOk && minOk && maxOk;
  }

  async function resolveOfferForAction(action) {
    const fallback = CONFIG.defaultOffer;

    try {
      const sourceVariant = await fetchSourceVariantInfo(action.variantId);
      const priceCents = Number(sourceVariant.price || 0);
      const variantId = Number(action.variantId || 0);

      const matchedRule = CONFIG.upsellRules.find((rule) =>
        ruleMatches(rule, { variantId, priceCents })
      );

      return matchedRule?.offer || fallback;
    } catch (err) {
      console.error('[UC UPSELL] resolveOfferForAction failed', err);
      return fallback;
    }
  }

  function injectStylesIfMissing() {
    if (document.getElementById('uc-upsell-modal-styles')) return;

    const style = document.createElement('style');
    style.id = 'uc-upsell-modal-styles';
    style.textContent = `
      /* UltraCore Systems • Compact crimson tech upsell */
      .uc-upsell-modal, .uc-upsell-modal * { box-sizing: border-box; }
      .uc-upsell-modal[hidden] { display: none !important; }
      .uc-upsell-modal {
        --uc-card-bg: ${CONFIG.cardBg};
        --uc-card-bg-2: ${CONFIG.cardBg2};
        --uc-card-text: ${CONFIG.textColor};
        --uc-card-muted: ${CONFIG.mutedTextColor};
        --uc-accent: ${CONFIG.accentColor};
        --uc-accent-hover: ${CONFIG.accentColor2};
        --uc-highlight: ${CONFIG.highlightColor};
        --uc-button-text: ${CONFIG.buttonTextColor};
        --uc-badge-bg: ${CONFIG.badgeBg};
        --uc-badge-border: ${CONFIG.badgeBorder};
        --uc-badge-text: ${CONFIG.badgeText};
        position: fixed;
        inset: 0;
        z-index: 2147483000;
        display: grid;
        place-items: center;
        padding: 14px;
        background: rgba(0, 0, 0, .77);
        -webkit-backdrop-filter: blur(8px);
        backdrop-filter: blur(8px);
        font-family: 'Rajdhani', 'Oxanium', system-ui, sans-serif;
        color: var(--uc-card-text);
      }
      .uc-upsell-modal__overlay {
        position: absolute;
        inset: 0;
        background: radial-gradient(ellipse at 50% 40%, rgba(200,16,46,.12), transparent 60%);
      }
      .uc-upsell-modal__dialog {
        position: relative;
        z-index: 1;
        width: min(100%, 472px);
        max-height: min(90dvh, 760px);
        overflow-x: hidden;
        overflow-y: auto;
        overscroll-behavior: contain;
        isolation: isolate;
        border: 1px solid rgba(200, 16, 46, .54);
        border-radius: 14px;
        background: linear-gradient(156deg, #17171B 0%, #0E0E11 49%, #080809 100%);
        box-shadow: 0 27px 85px rgba(0,0,0,.75), 0 0 0 1px rgba(255,255,255,.05) inset;
        animation: ucUpsellIn .22s ease-out both;
      }
      .uc-upsell-modal__dialog::before {
        content: '';
        position: absolute;
        top: 0; left: 13%; right: 13%; height: 2px;
        pointer-events: none;
        background: linear-gradient(90deg, transparent, var(--uc-accent), transparent);
      }
      .uc-upsell-modal__surface {
        position: absolute;
        inset: 0;
        pointer-events: none;
        z-index: 0;
        background: radial-gradient(ellipse at 50% 0%, rgba(200,16,46,.09), transparent 38%);
      }
      .uc-upsell-modal__inner {
        position: relative;
        z-index: 1;
        padding: 24px 27px 24px;
        text-align: center;
      }
      .uc-upsell-modal__close {
        position: absolute;
        top: 13px; right: 13px;
        width: 32px; height: 32px;
        display: grid; place-items: center;
        padding: 0;
        border: 1px solid rgba(255,255,255,.14);
        border-radius: 7px;
        background: #1E1E22;
        color: #EAEAF0;
        font: 400 23px/1 Arial,sans-serif;
        cursor: pointer;
        transition: background .18s ease, border-color .18s ease;
      }
      .uc-upsell-modal__close:hover { background: #30141C; border-color: var(--uc-accent); }
      .uc-upsell-modal__brand {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 5px;
        margin: 0 auto 14px;
      }
      .uc-upsell-modal__brand-logo {
        display: block;
        width: 72px;
        height: 52px;
        object-fit: contain;
        object-position: center;
        filter: drop-shadow(0 1px 12px rgba(255,255,255,.12));
      }
      .uc-upsell-modal__brand-name {
        display: flex;
        gap: 5px;
        justify-content: center;
        align-items: baseline;
        color: #F7F7F8;
        font: 900 10px/1 'Orbitron','Oxanium',sans-serif;
        letter-spacing: .13em;
        text-transform: uppercase;
      }
      .uc-upsell-modal__brand-name span { color: #B9B9C0; font-weight: 700; }
      .uc-upsell-modal__badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        max-width: 100%;
        margin: 0 auto 12px;
        padding: 6px 10px;
        border: 1px solid var(--uc-badge-border);
        border-radius: 4px;
        background: var(--uc-badge-bg);
        color: var(--uc-badge-text);
        font: 800 10px/1.2 'Oxanium',sans-serif;
        letter-spacing: .12em;
        text-transform: uppercase;
      }
      .uc-upsell-modal__badge::before {
        content: '';
        width: 6px; height: 6px;
        border-radius: 50%;
        background: var(--uc-accent);
        box-shadow: 0 0 9px rgba(200,16,46,.45);
      }
      .uc-upsell-modal__eyebrow {
        margin: 0 0 6px;
        color: #AFAFB4;
        font: 700 10px/1.35 'Oxanium',sans-serif;
        letter-spacing: .13em;
        text-transform: uppercase;
      }
      .uc-upsell-modal__title {
        margin: 0 auto;
        max-width: 395px;
        color: var(--uc-card-text);
        font: 900 clamp(21px, 4.7vw, 28px)/1.22 'Orbitron','Oxanium',sans-serif;
        letter-spacing: -.045em;
        text-transform: uppercase;
        text-wrap: balance;
      }
      .uc-upsell-modal__entries {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 2px;
        width: fit-content;
        max-width: 100%;
        min-width: 220px;
        margin: 17px auto 14px;
        padding: 13px 23px 11px;
        border: 1px solid rgba(200,16,46,.5);
        border-radius: 9px;
        background: linear-gradient(120deg, rgba(200,16,46,.16), rgba(255,255,255,.022) 60%, rgba(200,16,46,.07));
        box-shadow: inset 0 1px 0 rgba(255,255,255,.06), 0 10px 25px rgba(0,0,0,.25);
      }
      .uc-upsell-modal__entries-number {
        max-width: 100%;
        overflow-wrap: anywhere;
        color: #FFFFFF;
        font: 800 clamp(25px, 6vw, 36px)/1.08 'Oxanium',sans-serif;
        letter-spacing: -.06em;
        text-shadow: 0 0 20px rgba(200,16,46,.35);
      }
      .uc-upsell-modal__entries-label {
        color: #F0768A;
        font: 800 10px/1.2 'Oxanium',sans-serif;
        letter-spacing: .20em;
        text-transform: uppercase;
      }
      .uc-upsell-modal__line {
        height: 1px;
        margin: 16px 0 13px;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,.18), transparent);
      }
      .uc-upsell-modal__message {
        max-width: 390px;
        margin: 0 auto;
        color: #C7C7CD;
        font: 600 16px/1.42 'Rajdhani',system-ui,sans-serif;
      }
      .uc-upsell-modal__highlight { color: #FFFFFF; font-weight: 800; }
      .uc-upsell-modal__offer-price { color: #FFFFFF; font-weight: 800; white-space: nowrap; }
      .uc-upsell-modal__price-note {
        margin-top: 10px;
        color: #AFAFB4;
        font: 700 9px/1.4 'Oxanium',sans-serif;
        letter-spacing: .10em;
        text-transform: uppercase;
      }
      .uc-upsell-modal__actions {
        display: flex;
        flex-direction: column;
        gap: 9px;
        margin-top: 19px;
      }
      .uc-upsell-modal__btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        min-height: 44px;
        padding: 11px 15px;
        border-radius: 6px;
        font: 800 11px/1.35 'Oxanium',sans-serif;
        letter-spacing: .015em;
        text-align: center;
        text-transform: uppercase;
        cursor: pointer;
        transition: background-color .18s ease, border-color .18s ease, transform .18s ease;
      }
      .uc-upsell-modal__btn--yes {
        border: 1px solid #D62948;
        color: #FFFFFF;
        background: linear-gradient(105deg, #AC0E29, #C8102E 65%, #E11E42);
        box-shadow: 0 8px 22px rgba(200,16,46,.17);
      }
      .uc-upsell-modal__btn--yes:hover { background: linear-gradient(105deg, #C8102E, #E11E42); transform: translateY(-1px); }
      .uc-upsell-modal__btn--no {
        min-height: 39px;
        border: 1px solid rgba(255,255,255,.16);
        color: #C4C4CA;
        background: rgba(255,255,255,.035);
      }
      .uc-upsell-modal__btn--no:hover { border-color: rgba(255,255,255,.32); color: #FFFFFF; background: rgba(255,255,255,.06); }
      .uc-upsell-modal__btn:disabled, .uc-upsell-modal__close:disabled { cursor: wait; opacity: .55; }
      .uc-upsell-modal__btn:focus-visible, .uc-upsell-modal__close:focus-visible {
        outline: 2px solid #F7F7F8;
        outline-offset: 3px;
      }
      @keyframes ucUpsellIn {
        from { opacity: 0; transform: translateY(12px) scale(.988); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }
      @media (max-width: 480px) {
        .uc-upsell-modal { padding: 10px; }
        .uc-upsell-modal__dialog { max-height: calc(100dvh - 20px); border-radius: 11px; }
        .uc-upsell-modal__inner { padding: 19px 16px 19px; }
        .uc-upsell-modal__brand { margin-bottom: 10px; }
        .uc-upsell-modal__brand-logo { width: 62px; height: 44px; }
        .uc-upsell-modal__title { font-size: clamp(20px, 5.7vw, 25px); }
        .uc-upsell-modal__entries { margin-top: 14px; min-width: 190px; }
        .uc-upsell-modal__message { font-size: 15px; }
        .uc-upsell-modal__actions { margin-top: 15px; }
      }
      @media (max-height: 640px) {
        .uc-upsell-modal__inner { padding-top: 14px; padding-bottom: 14px; }
        .uc-upsell-modal__brand { margin-bottom: 7px; }
        .uc-upsell-modal__brand-logo { width: 56px; height: 38px; }
        .uc-upsell-modal__entries { margin-top: 10px; margin-bottom: 8px; padding: 9px 15px; }
        .uc-upsell-modal__line { margin-top: 9px; margin-bottom: 9px; }
        .uc-upsell-modal__actions { margin-top: 12px; }
      }
      @media (prefers-reduced-motion: reduce) {
        .uc-upsell-modal__dialog { animation: none; }
        .uc-upsell-modal__btn { transition: none; }
      }
      html.uc-upsell-lock, body.uc-upsell-lock { overflow: hidden !important; }
    `;
    document.head.appendChild(style);

    // Keep font styling self-contained when this JS is installed on Dawn.
    if (!document.getElementById('uc-upsell-modal-fonts')) {
      const fonts = document.createElement('link');
      fonts.id = 'uc-upsell-modal-fonts';
      fonts.rel = 'stylesheet';
      fonts.href = 'https://fonts.googleapis.com/css2?family=Orbitron:wght@700;800;900&family=Oxanium:wght@600;700;800&family=Rajdhani:wght@500;600;700&display=swap';
      document.head.appendChild(fonts);
    }
  }

  function buildModalMarkup() {
    const wrapper = document.createElement('div');
    wrapper.id = 'uc-upsell-modal';
    wrapper.className = 'uc-upsell-modal';
    wrapper.hidden = true;
    wrapper.innerHTML = `
      <div class="uc-upsell-modal__overlay" data-uc-upsell-overlay></div>
      <div class="uc-upsell-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="uc-upsell-title">
        <div class="uc-upsell-modal__surface" aria-hidden="true"></div>
        <div class="uc-upsell-modal__inner">
          <button type="button" class="uc-upsell-modal__close" id="uc-upsell-close" aria-label="Close">×</button>
          <div class="uc-upsell-modal__brand">
            ${CONFIG.logoUrl ? `<img class="uc-upsell-modal__brand-logo" id="uc-upsell-logo" src="${escapeHtml(CONFIG.logoUrl)}" alt="UltraCore Systems logo" width="72" height="52">` : ''}
            <div class="uc-upsell-modal__brand-name"><strong>ULTRACORE</strong><span>SYSTEMS</span></div>
          </div>
          <div class="uc-upsell-modal__badge" id="uc-upsell-badge">${escapeHtml(CONFIG.defaultOffer.badgeText)}</div>
          <div class="uc-upsell-modal__eyebrow">${escapeHtml(CONFIG.brandName)}</div>
          <h2 class="uc-upsell-modal__title" id="uc-upsell-title">${escapeHtml(CONFIG.defaultOffer.title)}</h2>

          <div class="uc-upsell-modal__entries" aria-label="Upsell entries">
            <span class="uc-upsell-modal__entries-number" id="uc-upsell-entries-number">Extra</span>
            <span class="uc-upsell-modal__entries-label">Entries</span>
          </div>

          <div class="uc-upsell-modal__line"></div>
          <p class="uc-upsell-modal__message" id="uc-upsell-message"></p>
          <div class="uc-upsell-modal__price-note" id="uc-upsell-price-note">${escapeHtml(CONFIG.brandName)}</div>

          <div class="uc-upsell-modal__actions">
            <button type="button" class="uc-upsell-modal__btn uc-upsell-modal__btn--yes" id="uc-upsell-yes">${escapeHtml(CONFIG.defaultOffer.yesLabel)}</button>
            <button type="button" class="uc-upsell-modal__btn uc-upsell-modal__btn--no" id="uc-upsell-no">${escapeHtml(CONFIG.defaultOffer.noLabel)}</button>
          </div>
        </div>
      </div>
    `;
    return wrapper;
  }

  function cacheMarkupRefs() {
    modalEl = document.getElementById('uc-upsell-modal');
    overlayEl = modalEl?.querySelector('[data-uc-upsell-overlay]') || null;
    messageEl = document.getElementById('uc-upsell-message');
    yesBtn = document.getElementById('uc-upsell-yes');
    noBtn = document.getElementById('uc-upsell-no');
    closeBtn = document.getElementById('uc-upsell-close');
    priceNoteEl = document.getElementById('uc-upsell-price-note');
    titleEl = document.getElementById('uc-upsell-title');
    badgeEl = document.getElementById('uc-upsell-badge');
    entriesNumberEl = document.getElementById('uc-upsell-entries-number');
    const logo = document.getElementById('uc-upsell-logo');
    if (logo) logo.addEventListener('error', () => { logo.style.display = 'none'; });
  }

  function injectMarkupIfMissing() {
    const existing = document.getElementById('uc-upsell-modal');
    if (existing) existing.remove();
    document.body.appendChild(buildModalMarkup());
    cacheMarkupRefs();
  }

  function setButtonsDisabled(disabled) {
    if (yesBtn) yesBtn.disabled = disabled;
    if (noBtn) noBtn.disabled = disabled;
    if (closeBtn) closeBtn.disabled = disabled;
  }

  function openModal() {
    if (!modalEl) return;
    modalEl.hidden = false;
    document.documentElement.classList.add('uc-upsell-lock');
    document.body.classList.add('uc-upsell-lock');
  }

  function closeModal() {
    if (modalEl) modalEl.hidden = true;
    forceUnlockPage();
  }

  function populateModalForOffer(offer, upsellVariantInfo) {
    if (badgeEl) badgeEl.textContent = offer.badgeText || CONFIG.defaultOffer.badgeText;
    if (titleEl) titleEl.textContent = offer.title || CONFIG.defaultOffer.title;
    if (yesBtn) yesBtn.textContent = offer.yesLabel || CONFIG.defaultOffer.yesLabel;
    if (noBtn) noBtn.textContent = offer.noLabel || CONFIG.defaultOffer.noLabel;

    const entriesFixed =
      upsellVariantInfo?.entriesFixed ??
      offer.entriesFixed ??
      null;

    const formattedEntries = formatEntriesText(entriesFixed);

    if (entriesNumberEl) {
      entriesNumberEl.textContent = formattedEntries;
    }

    if (messageEl) {
      messageEl.innerHTML =
        `Add a Digital Poster and <span class="uc-upsell-modal__highlight">${escapeHtml(formattedEntries)} extra entries</span> to this order for only <span class="uc-upsell-modal__offer-price">${escapeHtml(upsellVariantInfo.priceText)}</span>.`;
    }

    if (priceNoteEl) {
      priceNoteEl.textContent = offer.noteText || `${CONFIG.brandName} special add-on`;
    }
  }

  async function prepareAndShowModal(action) {
    currentAction = action;

    try {
      setButtonsDisabled(true);

      const selectedOffer = action?.selectedOffer || CONFIG.defaultOffer;
      const upsellVariantInfo = await fetchUpsellVariant(selectedOffer.upsellVariantId);

      populateModalForOffer(selectedOffer, upsellVariantInfo);
      openModal();
    } catch (err) {
      console.error('[UC UPSELL] Failed to prepare modal:', err);
      await executeAction(action, false);
    } finally {
      setButtonsDisabled(false);
    }
  }

  async function executeAction(action, includeUpsell) {
    if (!action || isProcessing) return;

    isProcessing = true;
    closeModal();

    if (!CONFIG.showEveryTime) {
      markUpsellSeenThisSession();
    }

    try {
      await nextFrame();

      const selectedUpsellVariantId = Number(action?.selectedOffer?.upsellVariantId || 0);
      const parentVariantId = Number(action.variantId);
      const parentQty = Number(action.quantity || 1);

      await addLinkedItemsToCart({
        parentVariantId,
        parentQty,
        childVariantId: selectedUpsellVariantId,
        includeChild: includeUpsell && !!selectedUpsellVariantId
      });

      if (window.UCCartDrawerSync?.refresh) {
        await window.UCCartDrawerSync.refresh({ openDrawer: !action.checkoutMode });
      } else {
        const sections = ['cart-drawer', 'cart-icon-bubble'];
        const url = `/?sections=${sections.join(',')}`;
        const res = await fetch(url, { credentials: 'same-origin' });
        const parsed = await res.json();

        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents({
            sections: {
              'cart-drawer': parsed['cart-drawer'],
              'cart-icon-bubble': parsed['cart-icon-bubble']
            }
          });
        }
      }

      if (action.checkoutMode) {
        window.location.href = '/checkout';
        return;
      }
    } catch (err) {
      console.error('[UC UPSELL] executeAction error', err);
      alert(err.message || 'There was a problem adding to cart.');
    } finally {
      currentAction = null;
      isProcessing = false;
      forceUnlockPage();
    }
  }

  async function handleIntercept(action) {
    if (!CONFIG.enabled) {
      await executeAction(action, false);
      return;
    }

    // Subscription / selling-plan products should bypass upsell modal completely
    if (action?.sellingPlanId) {
      submitNativeProductForm(action.form);
      return;
    }

    if (!shouldShowUpsellThisSession()) {
      await executeAction(action, false);
      return;
    }

    const selectedOffer = await resolveOfferForAction(action);

    if (!selectedOffer?.upsellVariantId) {
      await executeAction(action, false);
      return;
    }

    if (Number(action.variantId) === Number(selectedOffer.upsellVariantId)) {
      await executeAction(action, false);
      return;
    }

    action.selectedOffer = selectedOffer;
    await prepareAndShowModal(action);
  }

  function getMainVariantIdFromForm(form) {
    if (form && form.__variantId) return Number(form.__variantId) || null;
    if (!(form instanceof HTMLFormElement)) return null;

    const fd = new FormData(form);
    const id = fd.get('id');
    const n = Number(id);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  function getMainQtyFromForm(form) {
    if (form && form.__quantity) return Number(form.__quantity) || 1;
    if (!(form instanceof HTMLFormElement)) return 1;

    const fd = new FormData(form);
    const qty = Number(fd.get('quantity') || 1);
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  function buildActionFromForm(form, checkoutMode = false) {
    const variantId = getMainVariantIdFromForm(form);
    const quantity = getMainQtyFromForm(form);

    if (!variantId) return null;

    const fd = new FormData(form);
    const sellingPlanRaw = fd.get('selling_plan');
    const sellingPlanId = Number(sellingPlanRaw || 0) || null;

    return {
      type: checkoutMode ? 'dynamic_checkout_form' : 'form',
      variantId,
      quantity,
      checkoutMode,
      form,
      sellingPlanId
    };
  }

  function buildActionFromCustomButton(btn) {
    const variantId = Number(btn?.dataset?.variantId || 0);
    if (!variantId) return null;

    return {
      type: 'custom_button',
      variantId,
      quantity: 1,
      checkoutMode: false
    };
  }

  function bindModalEvents() {
    if (!yesBtn || !noBtn || !closeBtn) return;

    yesBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!currentAction || isProcessing) return;
      await executeAction(currentAction, true);
    });

    noBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!currentAction || isProcessing) return;
      await executeAction(currentAction, false);
    });

    closeBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!currentAction || isProcessing) return;
      await executeAction(currentAction, false);
    });

    if (overlayEl) {
      overlayEl.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!currentAction || isProcessing) return;
        await executeAction(currentAction, false);
      });
    }

    document.addEventListener('keydown', async (e) => {
      if (e.key !== 'Escape' || !currentAction || isProcessing || modalEl?.hidden) return;
      e.preventDefault();
      await executeAction(currentAction, false);
    });
  }

  function shouldInterceptStandardForm(form) {
    if (!form || form.dataset.ucUpsellBound === 'true') return false;
    if (!(form instanceof HTMLFormElement)) return false;
    if (!form.matches('form[action*="/cart/add"]')) return false;

    const variantId = getMainVariantIdFromForm(form);
    if (!variantId) return false;

    return !!form.querySelector('[type="submit"]');
  }

  function bindStandardForm(form) {
    if (!shouldInterceptStandardForm(form)) return;

    form.dataset.ucUpsellBound = 'true';

    form.addEventListener(
      'submit',
      async (e) => {
        if (form.dataset.ucUpsellBypassOnce === '1') {
          delete form.dataset.ucUpsellBypassOnce;
          return;
        }

        if (isProcessing) {
          e.preventDefault();
          e.stopPropagation();
          if (typeof e.stopImmediatePropagation === 'function') {
            e.stopImmediatePropagation();
          }
          return;
        }

        const action = buildActionFromForm(form, false);
        if (!action) return;

        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }

        await handleIntercept(action);
      },
      true
    );
  }

  function bindStandardForms(scope = document) {
    const forms = scope.querySelectorAll?.('form[action*="/cart/add"]');
    forms?.forEach(bindStandardForm);
  }

  function shouldInterceptCustomButton(btn) {
    if (!btn || btn.dataset.ucUpsellBound === 'true') return false;
    if (!(btn instanceof HTMLElement)) return false;
    if (!btn.matches('.uc-tabs-card__atc[data-variant-id], [data-variant-id].uc-tabs-card__atc')) return false;

    const variantId = Number(btn.dataset.variantId || 0);
    return !!variantId;
  }

  function bindCustomButton(btn) {
    if (!shouldInterceptCustomButton(btn)) return;

    btn.dataset.ucUpsellBound = 'true';

    btn.addEventListener('click', async (e) => {
      if (isProcessing) return;

      e.preventDefault();
      e.stopPropagation();

      const action = buildActionFromCustomButton(btn);
      if (!action) return;

      const textEl = btn.querySelector('.uc-atc-text');
      const loadingEl = btn.querySelector('.uc-atc-loading');

      try {
        btn.disabled = true;
        if (textEl) textEl.style.display = 'none';
        if (loadingEl) loadingEl.style.display = '';

        await handleIntercept(action);
      } finally {
        btn.disabled = false;
        if (textEl) textEl.style.display = '';
        if (loadingEl) loadingEl.style.display = 'none';
      }
    });
  }

  function bindCustomButtons(scope = document) {
    const buttons = scope.querySelectorAll?.('.uc-tabs-card__atc[data-variant-id], [data-variant-id].uc-tabs-card__atc');
    buttons?.forEach(bindCustomButton);
  }

  function isLikelyExpressTrigger(el) {
    if (!(el instanceof HTMLElement)) return false;

    return !!(
      el.matches('.shopify-payment-button__button') ||
      el.matches('.shopify-payment-button button') ||
      el.matches('shopify-payment-button button') ||
      el.matches('shop-pay-wallet-button') ||
      el.matches('shopify-accelerated-checkout') ||
      el.matches('[data-shopify="payment-button"]') ||
      el.matches('.shopify-payment-button') ||
      el.matches('more-payment-options-link a') ||
      el.closest('.shopify-payment-button') ||
      el.closest('[data-shopify="payment-button"]') ||
      el.closest('shopify-accelerated-checkout') ||
      el.closest('shop-pay-wallet-button') ||
      el.closest('more-payment-options-link')
    );
  }

  function bindShopPayButton(btn) {
    if (!btn || btn.dataset.ucUpsellBound === 'true') return;
    if (!isLikelyExpressTrigger(btn)) return;

    btn.dataset.ucUpsellBound = 'true';

    btn.addEventListener(
      'click',
      async (e) => {
        if (isProcessing) {
          e.preventDefault();
          e.stopPropagation();
          if (typeof e.stopImmediatePropagation === 'function') {
            e.stopImmediatePropagation();
          }
          return;
        }

        const form = btn.closest('form');
        const action = buildActionFromForm(form, true);
        if (!action) return;

        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }

        await handleIntercept(action);
      },
      true
    );
  }

  function bindShopPayButtons(scope = document) {
    const selectors = [
      '.shopify-payment-button__button',
      '.shopify-payment-button button',
      'shopify-payment-button button',
      'shop-pay-wallet-button',
      'shopify-accelerated-checkout',
      '[data-shopify="payment-button"]',
      '.shopify-payment-button',
      'more-payment-options-link a'
    ].join(',');

    const elements = scope.querySelectorAll?.(selectors);
    elements?.forEach(bindShopPayButton);
  }

  function bindGlobalExpressCapture() {
    if (expressClickBound) return;
    expressClickBound = true;

    document.addEventListener(
      'click',
      async (e) => {
        const trigger = e.target.closest(
          '.shopify-payment-button, [data-shopify="payment-button"], shopify-accelerated-checkout, shop-pay-wallet-button, more-payment-options-link a, .shopify-payment-button__button'
        );

        if (!trigger || isProcessing) return;

        const form = trigger.closest('form');
        const action = buildActionFromForm(form, true);
        if (!action) return;

        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }

        await handleIntercept(action);
      },
      true
    );
  }

  function observeNewNodes() {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;

          if (node.matches?.('form[action*="/cart/add"]')) bindStandardForm(node);
          if (node.matches?.('.uc-tabs-card__atc[data-variant-id], [data-variant-id].uc-tabs-card__atc')) bindCustomButton(node);
          if (isLikelyExpressTrigger(node)) bindShopPayButton(node);

          bindStandardForms(node);
          bindCustomButtons(node);
          bindShopPayButtons(node);
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function init() {
    if (!CONFIG.enabled || !window.UCCartDrawerSync) return;

    injectStylesIfMissing();
    injectMarkupIfMissing();
    bindModalEvents();

    bindStandardForms();
    bindCustomButtons();
    bindShopPayButtons();
    bindGlobalExpressCapture();

    observeNewNodes();
  }

  function createBundleGroup() {
    return `uc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  function buildParentProperties(bundleGroup) {
    return {
      _uc_bundle_group: bundleGroup,
      _uc_bundle_role: 'parent'
    };
  }

  function buildChildProperties(bundleGroup, parentVariantId) {
    return {
      _uc_bundle_group: bundleGroup,
      _uc_bundle_role: 'child',
      _uc_parent_variant_id: String(parentVariantId),
      _uc_offer_type: 'upsell'
    };
  }

  async function addLinkedItemsToCart({ parentVariantId, parentQty, childVariantId, includeChild }) {
    const bundleGroup = createBundleGroup();

    const items = [
      {
        id: Number(parentVariantId),
        quantity: Number(parentQty || 1),
        properties: buildParentProperties(bundleGroup)
      }
    ];

    if (includeChild && childVariantId) {
      items.push({
        id: Number(childVariantId),
        quantity: Number(parentQty || 1),
        properties: buildChildProperties(bundleGroup, parentVariantId)
      });
    }

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items })
    });

    const text = await res.text();

    if (!res.ok) {
      throw new Error(text || 'Failed to add linked items.');
    }

    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();